import React from 'react';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { AnimatePresence, motion } from 'framer-motion';
import { useMeasure } from 'common-react';
import { useTransition } from '../animations/Transition';
import { ReactComponent as ChevronLeftIcon } from 'common-assets/assets/icons/chevron-left-nav.svg';
import { ReactComponent as ChevronRightIcon } from 'common-assets/assets/icons/chevron-right-nav.svg';

import {
  TabChangeHandler,
  useDeferredTabValue,
  useTabs,
  useTabsIndicator,
  useTabsScroll,
} from './Tabs.hooks';
import { getTabA11yProps } from './Tabs.helpers';
import { useTranslation } from 'react-i18next';

import useStyles from './Tabs.style';

export interface CustomTabsProps {
  labels: string[];
  value: number;
  handleChange: TabChangeHandler;
  className?: string;
  additionalContent?: JSX.Element;
  disabled?: boolean;
  labelsKeys?: (string | undefined)[];
}

const CustomTabs:React.FC<CustomTabsProps> = ({
  labels,
  value,
  handleChange,
  className,
  additionalContent,
  disabled,
  labelsKeys,
}) => {
  const c = useStyles({});
  const { t } = useTranslation('components');
  const {
    wrapperRef,
    canScrollLeft,
    canScrollRight,
    handleScrollLeft,
    handleScrollRight,
    scrollTabIntoCenter,
  } = useTabsScroll();
  const { displayedValue, handleDeferredChange } = useDeferredTabValue(
    value,
    handleChange,
    scrollTabIntoCenter,
  );

  const tabsActions = useTabsIndicator(labels.length);

  return (
    <div className={c.tabsScrollWrapper} ref={wrapperRef}>
      {canScrollLeft && (
        <button
          type="button"
          className={`${c.scrollArrow} ${c.scrollArrowLeft}`}
          onClick={handleScrollLeft}
          aria-label={t('tabs.scroll-left-arrow-label')}
        >
          <ChevronLeftIcon className={c.scrollArrowIcon} />
        </button>
      )}
      <Tabs
        className={className}
        classes={{
          root: c.tabsInline,
          indicator: c.tabs__indicator,
        }}
        value={displayedValue}
        indicatorColor="primary"
        onChange={handleDeferredChange}
        aria-label="tabs"
        variant="scrollable"
        scrollButtons="off"
        action={tabsActions}
      >
        {labels.map((label, index) => (
          <Tab
            key={`${label}${index}`}
            classes={{
                root: c.item,
            }}
            label={label}
            disabled={disabled}
            {...getTabA11yProps(index)}
            {...(labelsKeys?.[index] ? { 'data-autotestid': labelsKeys[index] } : {})}
          />
        ))}
        {additionalContent != null && additionalContent}
      </Tabs>
      { canScrollRight && (
        <button
          type="button"
          className={`${c.scrollArrow} ${c.scrollArrowRight}`}
          onClick={handleScrollRight}
          aria-label={t('tabs.scroll-right-arrow-label')}
        >
          <ChevronRightIcon className={c.scrollArrowIcon} />
        </button>
      )}
    </div>
  );
};

interface TabPanelProps {
    className?: string;
    index: number;
    value: number;
}

const TabPanel:React.FC<TabPanelProps> = ({ className, children, value, index, ...otherProps }) => {
  const [ref, {  height }] = useMeasure();
  const { motionProps } = useTransition({
    yOffset: 10,
    duration: 0.2,
    direction: 'none',
  });

  const animateProps = {
    ...motionProps,
    initial: { ...motionProps.initial, height },
    exit: { ...motionProps.exit, height },
  };

  return (
    <AnimatePresence>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
        {...otherProps}
      >
        {value === index && (
        <div ref={ref}>
          <motion.div className={className} {...animateProps}>
            {children}
          </motion.div>
        </div>
        )}
      </div>
    </AnimatePresence>
  );
};

export { TabPanel, CustomTabs as Tabs, useTabs };
